import { router } from '@inertiajs/react'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { PlusCircleFilled, UserOutlined, TeamOutlined, CloseCircleOutlined } from '@ant-design/icons'
import EventLayout from '@/components/layouts/EventLayout'
import TabbedPage from '@/components/shared/TabbedPage'
import { ZeroState } from '@/components/patterns'
import { ConfirmModal, message } from '@/components/ui'
import RejectedCancelledTab from '@/components/attendees/RejectedCancelledTab'
import { ImportWizardModal } from '@/components/attendees/ImportWizard'
import BulkUpdateModal from '@/components/attendees/BulkUpdateModal'
import type { BulkUpdatePayload } from '@/components/attendees/BulkUpdateModal'
import { useAttendeeActions } from '@/hooks/useAttendeeActions'
import AttendeeTable from './components/AttendeeTable'
import AttendeeTableToolbar from './components/AttendeeTableToolbar'
import AttendeeProfileDrawer from './components/AttendeeProfileDrawer'
import AttendeeFilterModal from './components/AttendeeFilterModal'
import ManageColumnsDrawer from './components/ManageColumnsDrawer'
import AttendeeFormDrawer from './components/AttendeeFormDrawer'
import QrExportStatusModal, { type QrExportCounts } from './components/QrExportStatusModal'
import MarkAttendanceModal from './components/MarkAttendanceModal'
import type {
  Event,
  Attendee,
  RegistrationField,
  Pagination,
  Stats,
  FilterCondition,
  AttendeeStatus,
  ScanPointOption,
} from './types'
import type { BadgePrintLog, BadgeTemplate } from '@/types/badgeDesigner'
import { api, HttpError } from '@/lib/apiClient'
import type { UploadFile } from 'antd/es/upload/interface'
import { normalizeBadgeTemplate } from '@/lib/badgeTemplate'
import { useBadgePrinterSelection } from '@/hooks/useBadgePrinterSelection'
import { toPrintRegistrantPayload, triggerRegistrantBadgePrint } from '@/lib/registrantBadgePrint'

// ---------------------------------------------------------------------------
// XLSX async export — backend contract types
// ---------------------------------------------------------------------------

interface XlsxExportInitResponse {
  job_id: number
  status: string
  status_url?: string
}

interface XlsxExportJobResponse {
  id: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  format: string
  download_url?: string
  error?: string
}

// ---------------------------------------------------------------------------
// QR ZIP async export — backend contract types
// ---------------------------------------------------------------------------

interface QrExportInitResponse {
  job_id: number
  status: string
  status_url?: string
}

interface QrExportJobResponse {
  id: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  total_rows?: number
  // Present ONLY when status === 'completed'.
  download_url?: string
  // May be set on a failed job, or on a completed job with partial fetch failures.
  error_message?: string
}

// ---------------------------------------------------------------------------

interface IndexProps {
  event: Event
  attendees: Attendee[]
  rejected_cancelled: Attendee[]
  registration_fields: RegistrationField[]
  filters: any
  pagination: Pagination
  rejected_cancelled_pagination: Pagination
  stats: Stats
  badge_template?: BadgeTemplate | null
  badge_printing_settings?: {
    is_badge_printing_enabled: boolean
    is_print_triggers_check_in_enabled: boolean
  }
  scan_points: ScanPointOption[]
  default_scan_point_id?: number | null
}

export default function Index({
  event,
  attendees,
  rejected_cancelled,
  registration_fields,
  filters,
  pagination,
  rejected_cancelled_pagination,
  badge_template,
  badge_printing_settings,
  scan_points,
  default_scan_point_id,
}: IndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters?.search || '')
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [filterModalVisible, setFilterModalVisible] = useState(false)
  const [manageFieldsVisible, setManageFieldsVisible] = useState(false)

  // Advanced filtering state
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([])
  const [filterNameSearch, setFilterNameSearch] = useState('')

  // Add/Edit form state
  const [addDrawerVisible, setAddDrawerVisible] = useState(false)
  const [addFormValues, setAddFormValues] = useState<Record<string, any>>({})
  const [editFormValues, setEditFormValues] = useState<Record<string, any>>({})
  const [sendConfirmation, setSendConfirmation] = useState(false)

  // Bulk delete confirmation modal state
  const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false)
  const [pendingBulkDeleteIds, setPendingBulkDeleteIds] = useState<React.Key[]>([])

  // Bulk status change confirmation modal state
  const [bulkStatusModalVisible, setBulkStatusModalVisible] = useState(false)
  const [pendingBulkStatusAction, setPendingBulkStatusAction] = useState<'approve' | 'reject' | 'cancel' | null>(null)

  // Single attendee status change confirmation modal state
  const [singleStatusModalVisible, setSingleStatusModalVisible] = useState(false)
  const [pendingSingleStatusAction, setPendingSingleStatusAction] = useState<'reject' | 'cancel' | null>(null)
  const [pendingSingleStatusAttendeeId, setPendingSingleStatusAttendeeId] = useState<number | null>(null)

  // Import wizard state
  const [importWizardVisible, setImportWizardVisible] = useState(false)

  // Bulk update modal state
  const [bulkUpdateModalVisible, setBulkUpdateModalVisible] = useState(false)
  const badgePrintingEnabled = badge_printing_settings?.is_badge_printing_enabled ?? false
  const [printingAttendeeIds, setPrintingAttendeeIds] = useState<Set<number>>(new Set())

  // Manual attendance marking
  const [markAttendanceTarget, setMarkAttendanceTarget] = useState<Attendee | null>(null)
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false)
  const printingAttendeeIdsRef = useRef<Set<number>>(new Set())

  // XLSX export async state
  const [isExportingXlsx, setIsExportingXlsx] = useState(false)
  const xlsxPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const xlsxTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // QR ZIP export async state
  const [isExportingQr, setIsExportingQr] = useState(false)
  const qrPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const qrTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // QR export status-picker modal state. Opening this replaces the old direct
  // "Export QR codes (ZIP)" action so the user can scope the export by status.
  const [qrStatusModalOpen, setQrStatusModalOpen] = useState(false)

  // Local attendees state for real-time updates without page reload
  const [localAttendees, setLocalAttendees] = useState<Attendee[]>(attendees)
  const [localRejectedCancelled, setLocalRejectedCancelled] = useState<Attendee[]>(rejected_cancelled)

  // Sync local state when props change (e.g., after navigation)
  useEffect(() => {
    setLocalAttendees(attendees)
  }, [attendees])

  useEffect(() => {
    setLocalRejectedCancelled(rejected_cancelled)
  }, [rejected_cancelled])

  // Handler for real-time attendee updates (status changes)
  const handleAttendeeUpdate = (attendeeId: number, updates: Partial<Attendee>) => {
    const nextStatus = updates.status
    const isRejectedOrCancelled = nextStatus === 'rejected' || nextStatus === 'cancelled'
    const isApprovedOrPending = nextStatus === 'approved' || nextStatus === 'pending'

    const activeAttendee = localAttendees.find(a => a.id === attendeeId)
    const rejectedAttendee = localRejectedCancelled.find(a => a.id === attendeeId)
    const attendeeToUpdate = activeAttendee || rejectedAttendee

    if (nextStatus && isRejectedOrCancelled) {
      setLocalAttendees(prev => prev.filter(a => a.id !== attendeeId))
      if (attendeeToUpdate) {
        const updatedAttendee = { ...attendeeToUpdate, ...updates }
        setLocalRejectedCancelled(prev => {
          const exists = prev.some(a => a.id === attendeeId)
          if (exists) {
            return prev.map(a => (a.id === attendeeId ? updatedAttendee : a))
          }
          return [updatedAttendee, ...prev]
        })
      }
      return
    }

    if (nextStatus && isApprovedOrPending) {
      setLocalRejectedCancelled(prev => prev.filter(a => a.id !== attendeeId))
      if (attendeeToUpdate) {
        const updatedAttendee = { ...attendeeToUpdate, ...updates }
        setLocalAttendees(prev => {
          const exists = prev.some(a => a.id === attendeeId)
          if (exists) {
            return prev.map(a => (a.id === attendeeId ? updatedAttendee : a))
          }
          return [updatedAttendee, ...prev]
        })
      }
      return
    }

    if (activeAttendee) {
      setLocalAttendees(prev => prev.map(a => (a.id === attendeeId ? { ...a, ...updates } : a)))
    }

    if (rejectedAttendee) {
      setLocalRejectedCancelled(prev => prev.map(a => (a.id === attendeeId ? { ...a, ...updates } : a)))
    }
  }

  const handlePrintResult = (
    attendeeId: number,
    updates: { badge_print_logs: BadgePrintLog[]; badge_print_count: number; has_printed_badge: boolean }
  ) => {
    handleAttendeeUpdate(attendeeId, updates)
    attendeeActions.setSelectedAttendee((prev) =>
      prev?.id === attendeeId ? { ...prev, ...updates } : prev
    )
  }

  const normalizedBadgeTemplate = useMemo(
    () => (badge_template ? normalizeBadgeTemplate(badge_template) : null),
    [badge_template]
  )

  const {
    printerOptions,
    selectedPrinterUid,
    selectedPrinter,
    setSelectedPrinterUid,
    isLoadingPrinters,
    detectPrinters,
    hasActivePrinter,
    setupRequired,
    setupMessage,
  } = useBadgePrinterSelection(event.slug, {
    enabled: badgePrintingEnabled,
    autoDetect: badgePrintingEnabled,
    notifyWhenEmpty: false,
  })

  const printDisabledReason = useMemo(() => {
    if (!badgePrintingEnabled) return 'Badge printing disabled'
    if (!normalizedBadgeTemplate) return 'Badge template is not configured'
    if (!hasActivePrinter) return setupMessage || 'Pair a print station or select an agent printer'
    return undefined
  }, [badgePrintingEnabled, normalizedBadgeTemplate, hasActivePrinter, setupMessage])

  const isPrintEnabled =
    badgePrintingEnabled && hasActivePrinter && !setupRequired && Boolean(normalizedBadgeTemplate)

  const handleDirectPrint = async (attendee: Attendee) => {
    if (printingAttendeeIdsRef.current.has(attendee.id)) return

    if (!isPrintEnabled || !normalizedBadgeTemplate || !selectedPrinterUid) {
      message.warning(printDisabledReason || 'Print is not ready')
      return
    }

    printingAttendeeIdsRef.current.add(attendee.id)
    setPrintingAttendeeIds((prev) => {
      const next = new Set(prev)
      next.add(attendee.id)
      return next
    })

    const printerName = selectedPrinter?.printerName || selectedPrinter?.label
    const attendeePayload = {
      id: attendee.id,
      full_name: attendee.full_name,
      email: attendee.email,
      phone: attendee.contact_number,
      qr_value: attendee.qr_code,
      answers: attendee.custom_fields || {},
    }

    try {
      const response = await triggerRegistrantBadgePrint({
        eventSlug: event.slug,
        registrantId: attendee.id,
        printerUid: selectedPrinter?.printerUid || selectedPrinterUid,
        printerId: selectedPrinter?.printerId,
        stationId: selectedPrinter?.stationId,
        printStationPrinterId: selectedPrinter?.printStationPrinterId,
        printStationId: selectedPrinter?.printStationId,
        printerProfileId: selectedPrinter?.printerProfileId,
        printerName,
        template: normalizedBadgeTemplate,
        data: {
          registrant: toPrintRegistrantPayload(attendeePayload),
          event: {
            id: event.id,
            slug: event.slug,
            name: event.name,
            start_date: event.start_date,
            end_date: event.end_date,
            location: event.location,
          },
        },
      })

      handlePrintResult(attendee.id, {
        badge_print_logs: response.printLogs || [],
        badge_print_count: response.printedCount || 0,
        has_printed_badge: (response.printedCount || 0) > 0,
      })
      message.success('Badge print job queued')
    } catch (error: any) {
      message.error(error?.message || 'Failed to queue badge print')
    } finally {
      printingAttendeeIdsRef.current.delete(attendee.id)
      setPrintingAttendeeIds((prev) => {
        const next = new Set(prev)
        next.delete(attendee.id)
        return next
      })
    }
  }

  const handleMarkAttendance = async (scanPointId: number) => {
    if (!markAttendanceTarget) return

    const attendeeId = markAttendanceTarget.id
    setIsMarkingAttendance(true)
    try {
      const response = await api.post<{ attendee: Attendee; message: string }>(
        `/events/${event.slug}/attendees/${attendeeId}/mark_attendance`,
        { scan_point_id: scanPointId }
      )
      handleAttendeeUpdate(attendeeId, response.attendee)
      attendeeActions.setSelectedAttendee((prev) => (prev?.id === attendeeId ? response.attendee : prev))
      message.success(response.message)
      setMarkAttendanceTarget(null)
    } catch (error) {
      const data = error instanceof HttpError ? (error.data as { errors?: string[] } | null) : null
      message.error(data?.errors?.[0] || 'Failed to mark attendance')
    } finally {
      setIsMarkingAttendance(false)
    }
  }

  // Initialize visible columns dynamically from registration_fields
  const initializeVisibleColumns = () => {
    const columns: Record<string, boolean> = {}
    const coreFields = ['full_name', 'email', 'phone']
    let otherFieldsCount = 0

    registration_fields.forEach((field) => {
      if (coreFields.includes(field.identifier)) {
        columns[field.identifier] = true
      } else {
        if (otherFieldsCount < 3) {
          columns[field.identifier] = true
          otherFieldsCount++
        } else {
          columns[field.identifier] = false
        }
      }
    })
    columns['status'] = true
    columns['badge_print_status'] = true
    columns['created_at'] = true
    return columns
  }

  const initializeColumnOrder = () => {
    const priority = ['full_name', 'email', 'phone']
    const rest = registration_fields.map((f) => f.identifier).filter((id) => !priority.includes(id))
    return [...priority, ...rest, 'status', 'badge_print_status', 'created_at']
  }

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(initializeVisibleColumns())
  const [columnOrder, setColumnOrder] = useState<string[]>(initializeColumnOrder())

  const storageKey = useMemo(() => `ava-attendees-columns:${event.slug}`, [event.slug])

  const normalizeStoredPreferences = (
    defaults: { visible: Record<string, boolean>; order: string[] },
    stored: any
  ) => {
    const validKeys = new Set(defaults.order)
    const nextVisible = { ...defaults.visible }
    if (stored?.visibleColumns && typeof stored.visibleColumns === 'object') {
      Object.entries(stored.visibleColumns as Record<string, boolean>).forEach(([key, value]) => {
        if (validKeys.has(key)) {
          nextVisible[key] = Boolean(value)
        }
      })
    }

    const storedOrder: string[] = Array.isArray(stored?.columnOrder)
      ? stored.columnOrder.map((key: unknown) => String(key))
      : []
    const cleanedStored = storedOrder.filter((key) => validKeys.has(key))
    const missing = defaults.order.filter((key) => !cleanedStored.includes(key))
    const nextOrder = [...cleanedStored, ...missing]

    return { visible: nextVisible, order: nextOrder }
  }

  useEffect(() => {
    const defaults = {
      visible: initializeVisibleColumns(),
      order: initializeColumnOrder(),
    }

    if (typeof window === 'undefined') {
      setVisibleColumns(defaults.visible)
      setColumnOrder(defaults.order)
      return
    }

    const storedRaw = window.localStorage.getItem(storageKey)
    if (!storedRaw) {
      setVisibleColumns(defaults.visible)
      setColumnOrder(defaults.order)
      return
    }

    try {
      const parsed = JSON.parse(storedRaw)
      const normalized = normalizeStoredPreferences(defaults, parsed)
      setVisibleColumns(normalized.visible)
      setColumnOrder(normalized.order)
    } catch {
      setVisibleColumns(defaults.visible)
      setColumnOrder(defaults.order)
    }
  }, [registration_fields, storageKey])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const payload = JSON.stringify({ visibleColumns, columnOrder })
    window.localStorage.setItem(storageKey, payload)
  }, [visibleColumns, columnOrder, storageKey])

  const allAttendees = useMemo(() => {
    const byId = new Map<number, Attendee>()
    localAttendees.forEach((attendee) => byId.set(attendee.id, attendee))
    localRejectedCancelled.forEach((attendee) => {
      if (!byId.has(attendee.id)) {
        byId.set(attendee.id, attendee)
      }
    })
    return Array.from(byId.values())
  }, [localAttendees, localRejectedCancelled])

  // Use custom hooks for actions with real-time update callback
  const attendeeActions = useAttendeeActions(event.slug, allAttendees, {
    onAttendeeUpdate: handleAttendeeUpdate
  })

  // Initialize form values when editing attendee changes
  useEffect(() => {
    if (attendeeActions.editingAttendee) {
      const initialValues: Record<string, any> = {}
      registration_fields.forEach(field => {
        initialValues[field.identifier] = attendeeActions.editingAttendee?.custom_fields?.[field.identifier] || ''
      })
      setEditFormValues(initialValues)
    }
  }, [attendeeActions.editingAttendee])

  const handleSearch = () => {
    router.get(
      `/events/${event.slug}/attendees`,
      { search: searchTerm },
      { preserveState: true }
    )
  }

  const handleSearchReset = () => {
    setSearchTerm('')
    router.get(
      `/events/${event.slug}/attendees`,
      {},
      { preserveState: true }
    )
  }


  const applyFilters = () => {
    const params: any = {}

    if (filterNameSearch) {
      params.search = filterNameSearch
    }

    const validFilters = filterConditions.filter(f => f.field && f.value)
    if (validFilters.length > 0) {
      params.filters = validFilters
    }

    router.get(`/events/${event.slug}/attendees`, params, {
      preserveState: true,
      onSuccess: () => {
        setFilterModalVisible(false)
      }
    })
  }

  const resetFilters = () => {
    setFilterNameSearch('')
    setFilterConditions([])
    router.get(`/events/${event.slug}/attendees`, {}, {
      preserveState: true,
      onSuccess: () => {
        setFilterModalVisible(false)
      }
    })
  }

  const handleAddAttendee = () => {
    setAddFormValues({})
    setSendConfirmation(false)
    setAddDrawerVisible(true)
  }

  const buildFormData = (
    values: Record<string, any>,
    fields: RegistrationField[],
    opts: { update?: boolean } = {}
  ) => {
    const formData = new FormData()
    const prefix = opts.update ? 'attendee[answers]' : 'answers'

    // Derived basics
    const email = values.email
    const fullName = values.full_name || values.name
    const phone = values.phone || values.contact_number

    if (opts.update) {
      if (email) formData.append('attendee[email]', email)
      if (fullName) formData.append('attendee[full_name]', fullName)
      if (phone) formData.append('attendee[phone]', phone)
    } else {
      if (email) formData.append('email', email)
      if (fullName) formData.append('full_name', fullName)
    }

    fields.forEach((field) => {
      const value = values[field.identifier]
      if (value === undefined || value === null) return

      if (value && typeof value === 'object' && ('signed_id' in value || 'blob_id' in value)) {
        const signedId = (value as any).signed_id
        const blobId = (value as any).blob_id
        formData.append(`${prefix}[${field.identifier}]`, signedId || blobId)
        return
      }

      // File upload
      if (Array.isArray(value) && value.length > 0 && (value[0] as UploadFile)?.originFileObj) {
        const file = (value[0] as UploadFile).originFileObj as File | undefined
        if (file) formData.append(`${prefix}[${field.identifier}]`, file)
        return
      }

      // Multi select array
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(`${prefix}[${field.identifier}][]`, v))
        return
      }

      formData.append(`${prefix}[${field.identifier}]`, value)
    })

    return formData
  }

  const handleCreateAttendee = async () => {
    const email = addFormValues['email']

    const missingFields: string[] = []
    registration_fields.forEach(field => {
      if (field.required && !addFormValues[field.identifier]) {
        missingFields.push(field.label)
      }
    })

    if (missingFields.length > 0) {
      message.error(`Please fill in required fields: ${missingFields.join(', ')}`)
      return
    }

    if (!email) {
      message.error('Email is required')
      return
    }

    const formData = buildFormData(addFormValues, registration_fields, { update: false })
    formData.append('send_confirmation', String(sendConfirmation))

    try {
      await api.post(`/events/${event.slug}/attendees`, formData)
      message.success('Attendee added successfully')
      setAddDrawerVisible(false)
      setAddFormValues({})
      setSendConfirmation(false)
      router.reload()
    } catch (error: any) {
      const apiErrors = error?.data?.errors
      if (Array.isArray(apiErrors)) {
        message.error(apiErrors.join(', '))
      } else {
        message.error(error?.message || 'Failed to add attendee')
      }
    }
  }

  /**
   * Build query string from the currently-active server-side filters so that
   * both CSV and XLSX exports honour whatever the user has filtered by.
   *
   * `extraParams` lets callers append repeatable array-style params (e.g.
   * `{ 'status[]': ['pending', 'approved'] }` → `status[]=pending&status[]=approved`)
   * on top of the base search + filters[] params. Each value is `append`-ed so
   * multiple values under the same key are preserved.
   */
  const buildExportQueryString = useCallback(
    (extraParams?: Record<string, string[]>): string => {
      const params = new URLSearchParams()

      // `filters` is the raw server prop — it mirrors what the backend applied to
      // the current page view, so forwarding it gives a consistent export.
      if (filters?.search) {
        params.set('search', filters.search)
      }

      if (Array.isArray(filters?.filters)) {
        filters.filters.forEach((condition: FilterCondition, index: number) => {
          if (condition.field && condition.value) {
            params.set(`filters[${index}][field]`, condition.field)
            params.set(`filters[${index}][operator]`, condition.operator ?? '')
            params.set(`filters[${index}][value]`, condition.value)
          }
        })
      }

      if (extraParams) {
        Object.entries(extraParams).forEach(([key, values]) => {
          values.forEach((value) => params.append(key, value))
        })
      }

      const qs = params.toString()
      return qs ? `?${qs}` : ''
    },
    [filters]
  )

  /** Abort any in-flight XLSX polling timers (used on cleanup and on error). */
  const clearXlsxTimers = useCallback(() => {
    if (xlsxPollTimerRef.current !== null) {
      clearInterval(xlsxPollTimerRef.current)
      xlsxPollTimerRef.current = null
    }
    if (xlsxTimeoutRef.current !== null) {
      clearTimeout(xlsxTimeoutRef.current)
      xlsxTimeoutRef.current = null
    }
  }, [])

  /** Abort any in-flight QR ZIP polling timers (used on cleanup and on error). */
  const clearQrTimers = useCallback(() => {
    if (qrPollTimerRef.current !== null) {
      clearInterval(qrPollTimerRef.current)
      qrPollTimerRef.current = null
    }
    if (qrTimeoutRef.current !== null) {
      clearTimeout(qrTimeoutRef.current)
      qrTimeoutRef.current = null
    }
  }, [])

  /**
   * Run the QR codes (ZIP) async export scoped to the chosen attendee statuses.
   *
   * Mirrors the XLSX async flow but hits the QR enqueue/poll endpoints and
   * reuses the SAME filter builder so current filters/search apply. The selected
   * statuses are appended as repeatable `status[]=<key>` params on top of the
   * base search + filters[] query string. Invoked from the status-picker modal.
   */
  const runQrExport = useCallback(
    async (statuses: AttendeeStatus[]) => {
      if (isExportingQr) return // prevent double submit
      if (statuses.length === 0) return // Export button guards this too

      // Augment the base export query string with the selected statuses.
      const qs = buildExportQueryString({ 'status[]': statuses })

      setIsExportingQr(true)
      const qrLoadingKey = 'qr-export-loading'
      message.loading({ content: 'Preparing QR codes…', key: qrLoadingKey, duration: 0 })

      try {
        // Step 1 — enqueue the background QR export job. api.get sends
        // Accept: application/json, so the backend responds 202 JSON with a
        // job_id rather than streaming. A queue outage responds 503 { error }.
        const initResponse = await api.get<QrExportInitResponse>(
          `/events/${event.slug}/attendees/export_qr${qs}`
        )

        if (!initResponse.job_id || typeof initResponse.job_id !== 'number') {
          throw new Error('Failed to start QR code export.')
        }

        // Job accepted — close the status-picker modal so polling continues in
        // the background with the existing loading toast communicating progress.
        setQrStatusModalOpen(false)

        const qrPollUrl = `/events/${event.slug}/attendees/qr_export_jobs/${initResponse.job_id}.json`

        // Step 2 — poll the status endpoint every 2 seconds.
        const POLL_INTERVAL_MS = 2_000
        const MAX_WAIT_MS = 5 * 60 * 1_000 // 5 minutes

        const stopQrPolling = (success: boolean) => {
          clearQrTimers()
          setIsExportingQr(false)
          if (!success) {
            message.destroy(qrLoadingKey)
          }
        }

        // Hard timeout — prevents runaway polling if the job stalls.
        qrTimeoutRef.current = setTimeout(() => {
          stopQrPolling(false)
          message.error({ content: 'QR code export timed out. Please try again.', key: qrLoadingKey })
        }, MAX_WAIT_MS)

        qrPollTimerRef.current = setInterval(async () => {
          try {
            const job = await api.get<QrExportJobResponse>(qrPollUrl)

            if (job.status === 'completed' && job.download_url) {
              stopQrPolling(true)

              // A completed job may still carry error_message when some QR
              // fetches failed — surface that but still deliver the ZIP.
              if (job.error_message) {
                message.warning({
                  content: `QR codes ready with some errors: ${job.error_message}`,
                  key: qrLoadingKey,
                  duration: 6,
                })
              } else {
                message.success({ content: 'QR codes ready. Downloading…', key: qrLoadingKey, duration: 3 })
              }

              // Trigger download without navigating away from the page.
              const anchor = document.createElement('a')
              anchor.href = job.download_url
              anchor.download = ''
              document.body.appendChild(anchor)
              anchor.click()
              document.body.removeChild(anchor)

            } else if (job.status === 'failed') {
              stopQrPolling(false)
              message.error({
                content: job.error_message ?? 'QR code export failed. Please try again.',
                key: qrLoadingKey,
                duration: 6,
              })
            }
            // 'pending' | 'processing' — keep polling
          } catch (pollError: unknown) {
            stopQrPolling(false)
            const rawPollMsg = pollError instanceof Error ? pollError.message : ''
            const pollMsg =
              rawPollMsg && rawPollMsg.length < 200 && !rawPollMsg.includes('<')
                ? rawPollMsg
                : 'Failed to check QR export status. Please try again.'
            message.error({ content: pollMsg, key: qrLoadingKey, duration: 6 })
          }
        }, POLL_INTERVAL_MS)

      } catch (initError: unknown) {
        setIsExportingQr(false)
        clearQrTimers()

        // Prefer the backend's `error` field on a 503 queue outage.
        let initMsg = 'Failed to start QR code export.'
        if (
          initError instanceof HttpError &&
          initError.data &&
          typeof initError.data === 'object' &&
          'error' in initError.data &&
          typeof (initError.data as { error: unknown }).error === 'string'
        ) {
          initMsg = (initError.data as { error: string }).error
        } else if (initError instanceof Error) {
          const rawInitMsg = initError.message
          if (rawInitMsg && rawInitMsg.length < 200 && !rawInitMsg.includes('<')) {
            initMsg = rawInitMsg
          }
        }

        message.error({ content: initMsg, key: qrLoadingKey, duration: 6 })
      }
    },
    [event.slug, buildExportQueryString, clearQrTimers, isExportingQr]
  )

  /**
   * Fetch per-status QR export counts for the CURRENT search/filters (no status
   * scoping — the endpoint returns counts for all statuses). Reuses the shared
   * export query builder so counts always reflect the active view. Supplied to
   * the status-picker modal, which sums the selected statuses for a live total.
   */
  const fetchQrExportCounts = useCallback(
    async (): Promise<QrExportCounts> =>
      api.get<QrExportCounts>(
        `/events/${event.slug}/attendees/export_qr_count${buildExportQueryString()}`
      ),
    [event.slug, buildExportQueryString]
  )

  const handleExport = useCallback(async (format: 'csv' | 'xlsx' | 'qr') => {
    const qs = buildExportQueryString()

    // CSV: stream directly — unchanged behaviour.
    if (format === 'csv') {
      window.location.href = `/events/${event.slug}/attendees/export.csv${qs}`
      return
    }

    // QR (ZIP): open the status-picker modal. The actual enqueue/poll/download
    // flow runs in `runQrExport` once the user confirms their status selection.
    if (format === 'qr') {
      setQrStatusModalOpen(true)
      return
    }

    // XLSX: kick off background job, then poll for completion.
    if (isExportingXlsx) return // prevent double-click

    setIsExportingXlsx(true)
    const loadingKey = 'xlsx-export-loading'
    message.loading({ content: 'Generating Excel file…', key: loadingKey, duration: 0 })

    try {
      // Step 1 — initiate the background export job.
      // api.get sets Accept: application/json automatically, which tells the
      // backend to respond with JSON rather than streaming a file.
      const initResponse = await api.get<XlsxExportInitResponse>(
        `/events/${event.slug}/attendees/export.xlsx${qs}`
      )

      // Validate that we received a usable job_id before proceeding.
      // Do NOT rely on status_url from the server — construct the poll URL
      // client-side to avoid the `undefined` interpolation bug that occurs
      // when Inertia middleware or content-type negotiation drops/renames keys.
      if (!initResponse.job_id || typeof initResponse.job_id !== 'number') {
        throw new Error('Failed to start Excel export.')
      }

      const pollUrl = `/events/${event.slug}/attendees/export_jobs/${initResponse.job_id}.json`

      // Step 2 — poll the status endpoint every 2 seconds.
      const POLL_INTERVAL_MS = 2_000
      const MAX_WAIT_MS = 5 * 60 * 1_000 // 5 minutes

      const stopPolling = (success: boolean) => {
        clearXlsxTimers()
        setIsExportingXlsx(false)
        if (!success) {
          message.destroy(loadingKey)
        }
      }

      // Hard timeout — prevents runaway polling if the job stalls.
      xlsxTimeoutRef.current = setTimeout(() => {
        stopPolling(false)
        message.error({ content: 'Excel export timed out. Please try again.', key: loadingKey })
      }, MAX_WAIT_MS)

      xlsxPollTimerRef.current = setInterval(async () => {
        try {
          const job = await api.get<XlsxExportJobResponse>(pollUrl)

          if (job.status === 'completed' && job.download_url) {
            stopPolling(true)
            message.success({ content: 'Excel file ready. Downloading…', key: loadingKey, duration: 3 })

            // Trigger download without navigating away from the page.
            const anchor = document.createElement('a')
            anchor.href = job.download_url
            anchor.download = ''
            document.body.appendChild(anchor)
            anchor.click()
            document.body.removeChild(anchor)

          } else if (job.status === 'failed') {
            stopPolling(false)
            message.error({
              content: job.error ?? 'Excel export failed. Please try again.',
              key: loadingKey,
              duration: 6,
            })
          }
          // 'pending' | 'processing' — keep polling
        } catch (pollError: unknown) {
          stopPolling(false)
          const rawPollMsg = pollError instanceof Error ? pollError.message : ''
          const pollMsg =
            rawPollMsg && rawPollMsg.length < 200 && !rawPollMsg.includes('<')
              ? rawPollMsg
              : 'Failed to check export status. Please try again.'
          message.error({ content: pollMsg, key: loadingKey, duration: 6 })
        }
      }, POLL_INTERVAL_MS)

    } catch (initError: unknown) {
      setIsExportingXlsx(false)
      clearXlsxTimers()
      const rawInitMsg = initError instanceof Error ? initError.message : ''
      const initMsg =
        rawInitMsg && rawInitMsg.length < 200 && !rawInitMsg.includes('<')
          ? rawInitMsg
          : 'Failed to start Excel export.'
      message.error({ content: initMsg, key: loadingKey, duration: 6 })
    }
  }, [event.slug, buildExportQueryString, clearXlsxTimers, isExportingXlsx])

  // Clean up polling timers when the component unmounts.
  useEffect(() => {
    return () => {
      clearXlsxTimers()
      clearQrTimers()
    }
  }, [clearXlsxTimers, clearQrTimers])

  const handleBulkAction = (action: string) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select attendees first')
      return
    }

    if (action === 'delete') {
      setPendingBulkDeleteIds(selectedRowKeys)
      setBulkDeleteModalVisible(true)
    } else if (action === 'export_selected') {
      window.location.href = `/events/${event.slug}/attendees/bulk_update?attendee_ids=${selectedRowKeys.join(',')}&bulk_action=export_selected`
    } else if (action === 'approve' || action === 'reject' || action === 'cancel') {
      // Show confirmation modal for status changes
      setPendingBulkStatusAction(action)
      setBulkStatusModalVisible(true)
    } else {
      router.post(
        `/events/${event.slug}/attendees/bulk_update`,
        {
          attendee_ids: selectedRowKeys.map(key => Number(key)),
          bulk_action: action
        },
        {
          onSuccess: () => {
            setSelectedRowKeys([])
          },
          onError: () => {
            message.error('Failed to perform bulk action')
          }
        }
      )
    }
  }

  const handleConfirmBulkStatusChange = () => {
    if (!pendingBulkStatusAction) return

    router.post(
      `/events/${event.slug}/attendees/bulk_update`,
      {
        attendee_ids: selectedRowKeys.map(key => Number(key)),
        bulk_action: pendingBulkStatusAction
      },
      {
        onSuccess: () => {
          setSelectedRowKeys([])
          setBulkStatusModalVisible(false)
          setPendingBulkStatusAction(null)
        },
        onError: () => {
          message.error('Failed to perform bulk action')
        }
      }
    )
  }

  const handleBulkUpdateSubmit = async (payload: BulkUpdatePayload) => {
    try {
      const result = await api.post<{ success: boolean; message: string }>(`/events/${event.slug}/attendees/bulk_update`, payload)
      message.success(result.message || 'Attendees updated successfully')
      setSelectedRowKeys([])
      setBulkUpdateModalVisible(false)
      // Use router.visit to force a full page reload with fresh data
      router.visit(`/events/${event.slug}/attendees`, { preserveState: false })
    } catch (error: any) {
      message.error(error?.data?.message || error?.message || 'Failed to update attendees')
      throw error // Re-throw so BulkUpdateModal can handle it
    }
  }

  const toggleColumnVisibility = (columnKey: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }))
  }

  const handleConfirmBulkDelete = () => {
    router.post(
      `/events/${event.slug}/attendees/bulk_update`,
      {
        attendee_ids: pendingBulkDeleteIds.map(id => Number(id)),
        bulk_action: 'delete'
      },
      {
        onSuccess: () => {
          setSelectedRowKeys([])
          setBulkDeleteModalVisible(false)
          setPendingBulkDeleteIds([])
        },
        onError: () => {
          message.error('Failed to delete attendees')
        }
      }
    )
  }

  // Single attendee reject/cancel with confirmation
  const handleRejectClick = (attendeeId: number) => {
    setPendingSingleStatusAttendeeId(attendeeId)
    setPendingSingleStatusAction('reject')
    setSingleStatusModalVisible(true)
  }

  const handleCancelClick = (attendeeId: number) => {
    setPendingSingleStatusAttendeeId(attendeeId)
    setPendingSingleStatusAction('cancel')
    setSingleStatusModalVisible(true)
  }

  const handleConfirmSingleStatusChange = async () => {
    if (!pendingSingleStatusAttendeeId || !pendingSingleStatusAction) return

    if (pendingSingleStatusAction === 'reject') {
      await attendeeActions.handleReject(pendingSingleStatusAttendeeId)
    } else {
      await attendeeActions.handleCancel(pendingSingleStatusAttendeeId)
    }

    // Close modals and drawer
    setSingleStatusModalVisible(false)
    setPendingSingleStatusAction(null)
    setPendingSingleStatusAttendeeId(null)
    attendeeActions.setProfileDrawerVisible(false)
    attendeeActions.setSelectedAttendee(null)
  }

  const tabs = [
    {
      key: 'all',
      label: (
        <span className="flex items-center gap-2">
          <TeamOutlined />
          All Attendees
        </span>
      ),
      children: (
        <div className="bg-[#f3f5f8] min-h-[500px]">
          <div>
            {/* Toolbar */}
            <AttendeeTableToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSearch={handleSearch}
              onReset={handleSearchReset}
              onOpenFilter={() => setFilterModalVisible(true)}
              onOpenManageColumns={() => setManageFieldsVisible(true)}
              onExport={handleExport}
              isExportingXlsx={isExportingXlsx}
              isExportingQr={isExportingQr}
              onAddAttendee={handleAddAttendee}
              onOpenImport={() => setImportWizardVisible(true)}
              onOpenBulkUpdate={() => setBulkUpdateModalVisible(true)}
              selectedCount={selectedRowKeys.length}
              onBulkAction={handleBulkAction}
              showPrinterSelector={badgePrintingEnabled}
              printerOptions={printerOptions}
              selectedPrinterUid={selectedPrinterUid}
              printerLoading={isLoadingPrinters}
              onPrinterChange={(value) => setSelectedPrinterUid(value)}
              onRefreshPrinters={detectPrinters}
              printerSetupRequired={setupRequired}
              printerSetupMessage={setupMessage}
            />

            {/* Table or Zero State */}
            {localAttendees.length === 0 && !searchTerm ? (
              <ZeroState
                icon={<UserOutlined className="text-6xl text-gray-300" />}
                title="No Attendees Yet"
                description="Get started by adding your first attendee or importing from a file."
                action={{
                  label: 'Add Attendee',
                  icon: <PlusCircleFilled />,
                  onClick: handleAddAttendee
                }}
              />
            ) : (
              <AttendeeTable
                attendees={localAttendees}
                pagination={pagination}
                eventSlug={event.slug}
                registrationFields={registration_fields}
                visibleColumns={visibleColumns}
                columnOrder={columnOrder}
                selectedRowKeys={selectedRowKeys}
                onSelectionChange={setSelectedRowKeys}
                onViewProfile={attendeeActions.handleViewProfile}
                onEditAttendee={attendeeActions.handleEditAttendee}
                onDeleteAttendee={attendeeActions.handleDeleteClick}
                onPrintBadge={badgePrintingEnabled ? handleDirectPrint : undefined}
                isPrintEnabled={isPrintEnabled}
                printDisabledReason={printDisabledReason}
                printingAttendeeIds={printingAttendeeIds}
                onMarkAttendance={setMarkAttendanceTarget}
                markAttendanceDisabledReason={
                  scan_points.length === 0 ? 'Create a scan point first' : undefined
                }
                showDeleteAction={false}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'rejected_cancelled',
      label: (
        <span className="flex items-center gap-2">
          <CloseCircleOutlined />
          Rejected & Cancelled
        </span>
      ),
      children: (
        <RejectedCancelledTab
          attendees={localRejectedCancelled}
          pagination={rejected_cancelled_pagination}
          registrationFields={registration_fields}
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
          onViewProfile={attendeeActions.handleViewProfile}
          onEditAttendee={attendeeActions.handleEditAttendee}
          onDeleteAttendee={attendeeActions.handleDeleteClick}
          onPrintBadge={badgePrintingEnabled ? handleDirectPrint : undefined}
          isPrintEnabled={isPrintEnabled}
          printDisabledReason={printDisabledReason}
          printingAttendeeIds={printingAttendeeIds}
        />
      ),
    },
  ]

  return (
    <EventLayout>
      <div className="min-h-screen bg-[#f3f5f8] w-full">
        <TabbedPage
          title="Attendee Management"
          subtitle="Create, edit and delete attendees."
          tabs={tabs}
          defaultActiveKey="all"
        />

        <MarkAttendanceModal
          attendee={markAttendanceTarget}
          scanPoints={scan_points}
          defaultScanPointId={default_scan_point_id}
          loading={isMarkingAttendance}
          onClose={() => setMarkAttendanceTarget(null)}
          onConfirm={handleMarkAttendance}
        />

        {/* Profile Drawer */}
        <AttendeeProfileDrawer
          open={attendeeActions.profileDrawerVisible}
          onClose={() => {
            attendeeActions.setProfileDrawerVisible(false)
            attendeeActions.setSelectedAttendee(null)
          }}
          attendee={attendeeActions.selectedAttendee}
          registrationFields={registration_fields}
          onEdit={() => {
            attendeeActions.setProfileDrawerVisible(false)
            attendeeActions.handleEditAttendee(attendeeActions.selectedAttendee!.id)
          }}
          onPrintBadge={badgePrintingEnabled ? handleDirectPrint : undefined}
          isPrintEnabled={isPrintEnabled}
          printDisabledReason={printDisabledReason}
          isPrintingBadge={Boolean(
            attendeeActions.selectedAttendee?.id &&
            printingAttendeeIds.has(attendeeActions.selectedAttendee.id)
          )}
          onApprove={attendeeActions.handleApprove}
          onReject={handleRejectClick}
          onCancel={handleCancelClick}
        />

        {/* Manage Columns Drawer */}
        <ManageColumnsDrawer
          open={manageFieldsVisible}
          onClose={() => setManageFieldsVisible(false)}
          registrationFields={registration_fields}
          visibleColumns={visibleColumns}
          onToggleColumn={toggleColumnVisibility}
          columnOrder={columnOrder}
          onReorderColumns={setColumnOrder}
        />

        {/* Filter Modal */}
        <AttendeeFilterModal
          open={filterModalVisible}
          onClose={() => setFilterModalVisible(false)}
          registrationFields={registration_fields}
          filterNameSearch={filterNameSearch}
          filterConditions={filterConditions}
          onFilterNameSearchChange={setFilterNameSearch}
          onFilterConditionsChange={setFilterConditions}
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
        />

        {/* Add Attendee Drawer */}
        <AttendeeFormDrawer
          title="Add Attendee"
          open={addDrawerVisible}
          onClose={() => setAddDrawerVisible(false)}
          onSubmit={handleCreateAttendee}
          submitText="Add Attendee"
          registrationFields={registration_fields}
          formValues={addFormValues}
          eventSlug={event.slug}
          onFieldChange={(identifier, value) => {
            setAddFormValues(prev => ({ ...prev, [identifier]: value }))
          }}
          showSendConfirmation={true}
          sendConfirmation={sendConfirmation}
          onSendConfirmationChange={setSendConfirmation}
        />

        {/* Edit Attendee Drawer */}
        {attendeeActions.editingAttendee && (
          <AttendeeFormDrawer
            title="Edit Profile"
            open={attendeeActions.editDrawerVisible}
            onClose={attendeeActions.closeEditDrawer}
            onSubmit={() => {
              attendeeActions.handleUpdateAttendee({ answers: editFormValues, id: attendeeActions.editingAttendee?.id })
            }}
            submitText="Save Changes"
            registrationFields={registration_fields}
            formValues={editFormValues}
            eventSlug={event.slug}
            onFieldChange={(identifier, value) => {
              setEditFormValues(prev => ({ ...prev, [identifier]: value }))
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          title="Delete Attendee"
          open={attendeeActions.deleteModalVisible}
          onConfirm={attendeeActions.handleDeleteAttendee}
          onClose={() => {
            attendeeActions.setDeleteModalVisible(false)
            attendeeActions.setAttendeeToDelete(null)
          }}
          confirmText="Delete"
          danger
        >
          <p>Are you sure you want to delete {attendeeActions.attendeeToDelete?.full_name}?</p>
          <p className="text-gray-500 text-sm mt-2">This action cannot be undone.</p>
        </ConfirmModal>

        {/* Bulk Delete Confirmation Modal */}
        <ConfirmModal
          title="Delete Selected Attendees"
          open={bulkDeleteModalVisible}
          onConfirm={handleConfirmBulkDelete}
          onClose={() => {
            setBulkDeleteModalVisible(false)
            setPendingBulkDeleteIds([])
          }}
          confirmText="Delete"
          danger
        >
          <p>Are you sure you want to delete {pendingBulkDeleteIds.length} attendee(s)?</p>
          <p className="text-gray-500 text-sm mt-2">This action cannot be undone.</p>
        </ConfirmModal>

        {/* Bulk Status Change Confirmation Modal */}
        <ConfirmModal
          title={
            pendingBulkStatusAction === 'approve'
              ? 'Approve Selected Attendees'
              : pendingBulkStatusAction === 'reject'
                ? 'Reject Selected Attendees'
                : 'Cancel Selected Registrations'
          }
          open={bulkStatusModalVisible}
          onConfirm={handleConfirmBulkStatusChange}
          onClose={() => {
            setBulkStatusModalVisible(false)
            setPendingBulkStatusAction(null)
          }}
          confirmText={
            pendingBulkStatusAction === 'approve'
              ? 'Approve'
              : pendingBulkStatusAction === 'reject'
                ? 'Reject'
                : 'Cancel Selected Registrations'
          }
          danger={pendingBulkStatusAction === 'reject' || pendingBulkStatusAction === 'cancel'}
        >
          <p>
            Are you sure you want to {pendingBulkStatusAction} {selectedRowKeys.length} attendee(s)?
          </p>
          {pendingBulkStatusAction === 'approve' && (
            <p className="text-gray-500 text-sm mt-2">
              Only registrations that are not already approved will be approved.
            </p>
          )}
          {pendingBulkStatusAction === 'reject' && (
            <p className="text-gray-500 text-sm mt-2">
              Only pending registrations will be rejected. You can re-approve later.
            </p>
          )}
          {pendingBulkStatusAction === 'cancel' && (
            <p className="text-gray-500 text-sm mt-2">
              You can re-approve later if needed.
            </p>
          )}
        </ConfirmModal>

        {/* Single Attendee Status Change Confirmation Modal */}
        <ConfirmModal
          title={
            pendingSingleStatusAction === 'reject'
              ? 'Reject Registration'
              : 'Cancel Registration'
          }
          open={singleStatusModalVisible}
          onConfirm={handleConfirmSingleStatusChange}
          onClose={() => {
            setSingleStatusModalVisible(false)
            setPendingSingleStatusAction(null)
            setPendingSingleStatusAttendeeId(null)
          }}
          confirmText={
            pendingSingleStatusAction === 'reject'
              ? 'Reject'
              : 'Cancel Registration'
          }
          danger
          zIndex={1100}
        >
          <p>
            Are you sure you want to {pendingSingleStatusAction === 'reject' ? 'reject' : 'cancel'} this registration?
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {pendingSingleStatusAction === 'reject'
              ? 'This attendee will be moved to the Rejected & Cancelled list.'
              : 'This attendee will be moved to the Rejected & Cancelled list.'}
          </p>
        </ConfirmModal>

        {/* Import Wizard Modal */}
        <ImportWizardModal
          open={importWizardVisible}
          onClose={() => setImportWizardVisible(false)}
          eventSlug={event.slug}
          registrationFields={registration_fields}
          onImportComplete={() => {
            router.reload()
          }}
        />

        {/* Bulk Update Modal */}
        <BulkUpdateModal
          open={bulkUpdateModalVisible}
          onClose={() => setBulkUpdateModalVisible(false)}
          selectedCount={selectedRowKeys.length}
          selectedIds={selectedRowKeys
            .filter((key): key is string | number => typeof key === 'string' || typeof key === 'number')}
          registrationFields={registration_fields}
          eventSlug={event.slug}
          onSubmit={handleBulkUpdateSubmit}
        />

        {/* QR Export Status Picker Modal */}
        <QrExportStatusModal
          open={qrStatusModalOpen}
          onClose={() => setQrStatusModalOpen(false)}
          onConfirm={runQrExport}
          loading={isExportingQr}
          fetchCounts={fetchQrExportCounts}
        />

      </div>
    </EventLayout>
  )
}
